<template>
  <div id="app" v-if="bootChecked">
    <section class="w-full relative">
      <div v-if="!route.meta.authUserRoutes" class="half-white"></div>
      <div v-if="!route.meta.authUserRoutes" class="image-container"></div>
      <Navbar id="navbar" v-if="!route.meta.requiresAuth" />
      <Topbar
        id="topbar"
        v-if="route.meta.requiresAuth"
        class="ml-0 lg:ml-[350px]"
        :openSidebar="openSidebar"
        :closeSidebar="closeSidebar"
      />
      <Sidebar
        id="sidebar"
        v-if="route.meta.requiresAuth"
        class="lg:ml-0 lg:w-[350px]"
        :class="{ '-ml-[350px]': sidebar === false }"
        :closeSidebar="closeSidebar"
      />
      <main
        :class="
          route.meta.requiresAuth
            ? 'lg:ml-[350px] ml-0 bg-white p-[20px] lg:p-[40px] mainView'
            : 'p-5 lg:px-[40px] xl:px-[100px]'
        "
      >
        <RouterView class="container mx-auto"></RouterView>
        <footer
          v-if="route.meta.showFooter && !route.meta.authUserRoutes"
          class="max-[481px]:mt-[10px] flex justify-center items-center gap-[14px] lg:pb-[130px] lg:mt-5"
        >
          <img src="./assets/images/logo.png" width="40" height="40" />
          <img src="./assets/images/mfu_logo.png" width="40" height="40" />
          <img src="./assets/images/triangle_logo.png" width="40" height="40" />
          <img src="./assets/images/elephant_logo.png" width="40" height="40" />
          <img
            src="./assets/images/public_health_logo.png"
            width="40"
            height="40"
          />
          <img src="./assets/images/NIRR.png" width="40" height="40" />
        </footer>
      </main>
    </section>
    <StatusModal ref="statusModal" />
    <Loader id="loader" class="loader fixed top-0" ref="loader" />
  </div>
</template>
<script setup>
import apiClient, { registerLoaderRef } from "./components/Utilities/javascripts/axios";
import StatusModal from "./components/Modals/StatusModal.vue";
import Topbar from "./components/Layout/Topbar.vue";
import Navbar from "./components/Layout/Navbar.vue";
import Sidebar from "./components/Layout/Sidebar.vue";
import Loader from "./components/Layout/Loader.vue";
import { useAuthStore } from "./pinia/authStore";
import VueCookies from "vue-cookies";
import { provide, onMounted, onUnmounted, computed, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const loader = ref(null);
const sidebar = ref(Boolean);
const statusModal = ref(null);
const bootChecked = ref(false);
const authStore = useAuthStore();
provide("statusModal", statusModal);

const routesWithoutLayout = ["/dashboard", "/disaster"];

// ตรวจสอบว่าควรแสดงองค์ประกอบของเลย์เอาต์หรือไม่
const ShowElements = computed(() => {
  return !routesWithoutLayout.includes(route.path);
});

const showSidebar = computed(() => {
  return route.meta.requiresAuth;
});

const openSidebar = () => {
  sidebar.value = true;
};

const closeSidebar = () => {
  sidebar.value = false;
};

provide("validateMsg", (errorObj) => {
  let errorMessages = null;
  if (Array.isArray(errorObj)) {
    errorMessages = {};
    Object.keys(errorMessages).forEach(function (key) {
      errorMessages[key] = "";
    });
    Object.keys(errorObj).forEach(function (key) {
      errorMessages[key] = errorObj[key][0];
    });
  } else {
    errorMessages = errorObj;
  }
  return errorMessages;
});

provide("loader", loader);

const ONLINE_STATUS_KEY = "app_online_status";
const ONLINE_STATUS_EVENT = "app-online-status";
const PROBE_TIMEOUT_MS = 3000;
const ONLINE_RETRY_DELAY_MS = 1500;
const RECONNECT_POLL_MS = 3000;
let reconnectRetryTimer = null;
let reconnectPollTimer = null;
let reconnectRunning = false;

const resolveProbeBaseUrl = () => {
  const runtimeHost = window.location.origin;
  return String(runtimeHost || window.location.origin).replace(/\/+$/, "");
};

const publishOnlineStatus = (online) => {
  const next = Boolean(online);
  try {
    localStorage.setItem(ONLINE_STATUS_KEY, next ? "1" : "0");
  } catch {
    // ignore storage errors
  }
  window.dispatchEvent(
    new CustomEvent(ONLINE_STATUS_EVENT, { detail: { online: next } })
  );
};

const probeBackendOnline = async () => {
  if (!navigator.onLine) {
    publishOnlineStatus(false);
    return false;
  }

  const url = `${resolveProbeBaseUrl()}/api?_probe=${Date.now()}`;
  try {
    const response = await apiClient.get(url, {
      timeout: PROBE_TIMEOUT_MS,
      headers: {
        "X-No-Loader": "true",
      },
      // ขอแค่ติดต่อ backend ได้ แม้ได้ 404/401 ก็ยังนับว่า online
      validateStatus: () => true,
    });

    const reachable = typeof response?.status === "number" && response.status > 0;
    publishOnlineStatus(reachable);
    return reachable;
  } catch {
    publishOnlineStatus(false);
    return false;
  }
};

const refetchProfileIfLoggedIn = async () => {
  const token = VueCookies.get("token");
  if (!token) return;
  try {
    await authStore.getProfile();
  } catch (error) {
    console.log("Refetch profile on reconnect failed:", error);
  }
};

const clearReconnectRetry = () => {
  if (reconnectRetryTimer !== null) {
    clearTimeout(reconnectRetryTimer);
    reconnectRetryTimer = null;
  }
};

const clearReconnectPolling = () => {
  if (reconnectPollTimer !== null) {
    clearInterval(reconnectPollTimer);
    reconnectPollTimer = null;
  }
};

const tryRecoverOnline = async () => {
  if (reconnectRunning) return;
  reconnectRunning = true;

  try {
    const online = await probeBackendOnline();
    if (!online) return;

    clearReconnectRetry();
    clearReconnectPolling();
    await refetchProfileIfLoggedIn();
  } finally {
    reconnectRunning = false;
  }
};

const ensureReconnectPolling = () => {
  if (reconnectPollTimer !== null) return;

  reconnectPollTimer = window.setInterval(() => {
    void tryRecoverOnline();
  }, RECONNECT_POLL_MS);
};

const scheduleReconnectRetry = () => {
  if (reconnectRetryTimer !== null) return;

  reconnectRetryTimer = window.setTimeout(() => {
    reconnectRetryTimer = null;
    void tryRecoverOnline();
  }, ONLINE_RETRY_DELAY_MS);
};

const handleBrowserOnline = async () => {
  const online = await probeBackendOnline();
  if (!online) {
    // บางเครื่อง event online มาก่อน backend พร้อมใช้งานจริง
    scheduleReconnectRetry();
    ensureReconnectPolling();
    return;
  }
  clearReconnectRetry();
  clearReconnectPolling();
  // window.location.reload();
  await refetchProfileIfLoggedIn();
};
const handleBrowserOffline = () => {
  clearReconnectRetry();
  publishOnlineStatus(false);
  ensureReconnectPolling();
};

const initOnlineBeforeRender = async () => {
  const online = await probeBackendOnline();
  if (!online) {
    ensureReconnectPolling();
  }
  bootChecked.value = true;
};

const handleWindowFocus = () => {
  void tryRecoverOnline();
};

// เช็คก่อน render ทั้งหน้า
void initOnlineBeforeRender();

// ผูก listener ตั้งแต่ setup กันพลาด event ช่วงเริ่มต้นแอป
window.addEventListener("online", handleBrowserOnline);
window.addEventListener("offline", handleBrowserOffline);
window.addEventListener("focus", handleWindowFocus);

onMounted(() => {
  registerLoaderRef(loader);
});

onUnmounted(() => {
  clearReconnectRetry();
  clearReconnectPolling();
  window.removeEventListener("online", handleBrowserOnline);
  window.removeEventListener("offline", handleBrowserOffline);
  window.removeEventListener("focus", handleWindowFocus);
});
</script>

<style scoped>
.image-container {
  background: url("/images/background-img.jpg") lightgray 50% / cover no-repeat;
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: -2;
  transform: scale(1.1);
  left: 0;
  top: 0;
  @media (max-width: 1024px) {
    display: none;
  }
}

.half-white {
  position: fixed;
  /* margin-top: 90px; */
  width: 100%;
  height: 70%;
  background: linear-gradient(180deg, white, #ffffff00);
  z-index: -1;
}

.sidebar-fixed {
  width: 260px;
  min-height: 100vh;
  position: sticky;
  top: 0;
  z-index: 2;
}

.content-with-sidebar {
  width: calc(100% - 260px);
  /* ปรับตามความกว้างของ Sidebar */
  padding-left: 1rem;
}

.mainView {
  min-height: calc(100vh - 91px);
   @media (min-width: 40rem) {
    padding-bottom: 50px;
  }
}
</style>
